// Adapted from @ag-grid-community/vue v31.3.4 (community-modules/vue/src/AgGridVue.ts),
// MIT License, Copyright (c) 2015-2019 AG GRID LTD.
// https://github.com/ag-grid/ag-grid/blob/latest/LICENSE.txt
//
// Rewritten as a plain Vue 2 Options API object instead of a
// vue-property-decorator class (`class AgGridVue extends Vue`). The upstream
// package defines that class at its own module top level, which requires a
// real Vue constructor to exist the moment the module is imported - fine for
// normal npm/bundler consumption, but incompatible with mead's standalone
// <script> bundle, which must stay safe to load even when no Vue global
// exists yet. A plain options object has no such requirement: Vue only needs
// to exist once the component is actually registered/mounted.
//
// Keep this in sync with @ag-grid-community/vue on every ag-grid version
// bump - diff community-modules/vue/src/AgGridVue.ts (and its Utils.ts,
// VueFrameworkComponentWrapper.ts, VueFrameworkOverrides.ts, and
// VueComponentFactory.ts siblings) against this file and
// node_modules/@ag-grid-community/vue/dist/package/main.esm.mjs.
//
// The upstream @Bean("agGridVue")/@Bean("frameworkComponentWrapper") DI
// decorators are intentionally omitted here: both AgGridVue and
// VueFrameworkComponentWrapper are wired up via plain object
// instantiation/assignment in mounted() (providedBeanInstances), not via
// ag-grid's bean-scanning, so that decorator metadata is unused in this
// specific code path.
//
// Deliberately no top-level `import Vue from 'vue'` (see above): consumers
// of the standalone bundle may load it before window.Vue exists, and a
// module-scope Vue import gets captured once, permanently, at that moment
// (mead's bundle externalizes `vue` - see vite.bundle.config.ts - so its UMD
// wrapper resolves `import Vue from 'vue'` to whatever window.Vue was at
// load time, not a live binding). getComponentType() below instead reads
// `parent.$options._base`, Vue's own internal handle (used by Vue core
// itself for this same purpose, e.g. resolving async components) on every
// live component instance for the base constructor it was actually created
// from - always correct regardless of when/whether Vue was global yet when
// this module first loaded.
import {
    BaseComponentWrapper,
    ComponentUtil,
    VanillaFrameworkOverrides,
    createGrid,
    ALWAYS_SYNC_GLOBAL_EVENTS,
} from '@ag-grid-community/core';

const ROW_DATA_EVENTS = new Set(['rowDataUpdated', 'cellValueChanged', 'rowValueChanged']);

function kebabProperty (property) {
    return property.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

class VueComponentFactory {
    static getComponentType (parent, component) {
        if (typeof component === 'string') {
            const componentInstance = this.searchForComponentInstance(parent, component);
            if (!componentInstance) {
                console.error(`Could not find component with name of ${component}. Is it in Vue.components?`);
                return null;
            }
            return parent.$options._base.extend(componentInstance);
        } else {
            return component;
        }
    }

    static createAndMountComponent (params, componentType, parent) {
        const details = {
            data: {
                params: Object.freeze(params),
            },
            parent,
        };
        if (parent.componentDependencies) {
            parent.componentDependencies.forEach((dependency) => {
                details[dependency] = parent[dependency];
            });
        }
        const component = new componentType(details);
        component.$mount();
        return component;
    }

    static searchForComponentInstance (parent, component, maxDepth = 10, suppressError = false) {
        let componentInstance = null;
        let currentParent = parent.$parent;
        let depth = 0;
        while (!componentInstance && currentParent && currentParent.$options && ++depth < maxDepth) {
            if (currentParent.$options && currentParent.$options.components && currentParent.$options.components[component]) {
                componentInstance = currentParent.$options.components[component];
            } else if (currentParent[component]) {
                componentInstance = currentParent[component];
            }
            currentParent = currentParent.$parent;
        }
        if (!componentInstance && !suppressError) {
            console.error(`Could not find component with name of ${component}. Is it in Vue.components?`);
            return null;
        }
        return componentInstance;
    }
}

class VueComponent {
    getGui () {
        return this.component.$el;
    }
    destroy () {
        if (this.getFrameworkComponentInstance() && typeof this.getFrameworkComponentInstance().destroy === 'function') {
            this.getFrameworkComponentInstance().destroy();
        }
        this.component.$destroy();
    }
    getFrameworkComponentInstance () {
        return this.component;
    }
    init (params) {
        this.component = this.createComponent(params);
    }
}

class VueFrameworkComponentWrapper extends BaseComponentWrapper {
    constructor (parent) {
        super();
        this.parent = parent;
    }
    createWrapper (component) {
        const that = this;
        class DynamicComponent extends VueComponent {
            init (params) {
                super.init(params);
            }
            hasMethod (name) {
                return wrapper.getFrameworkComponentInstance()[name] != null;
            }
            callMethod (name, args) {
                const componentInstance = this.getFrameworkComponentInstance();
                const frameworkComponentInstance = wrapper.getFrameworkComponentInstance();
                return frameworkComponentInstance[name].apply(componentInstance, args);
            }
            addMethod (name, callback) {
                wrapper[name] = callback;
            }
            overrideProcessing (methodName) {
                return that.parent.autoParamsRefresh && methodName === 'refresh';
            }
            processMethod (methodName, args) {
                if (methodName === 'refresh') {
                    this.getFrameworkComponentInstance().params = args[0];
                }
                if (this.hasMethod(methodName)) {
                    return this.callMethod(methodName, args);
                }
                return methodName === 'refresh';
            }
            createComponent (params) {
                return that.createComponent(component, params);
            }
        }
        const wrapper = new DynamicComponent();
        return wrapper;
    }
    createComponent (component, params) {
        const componentType = VueComponentFactory.getComponentType(this.parent, component);
        if (!componentType) return;
        return VueComponentFactory.createAndMountComponent(params, componentType, this.parent);
    }
    createMethodProxy (wrapper, methodName, mandatory) {
        return function (...args) {
            if (wrapper.overrideProcessing(methodName)) {
                return wrapper.processMethod(methodName, args);
            }
            if (wrapper.hasMethod(methodName)) {
                return wrapper.callMethod(methodName, args);
            }
            if (mandatory) {
                console.warn(`AG Grid: Framework component is missing the method ${methodName}()`);
            }
            return null;
        };
    }
    destroy () {
        this.parent = null;
    }
}

class VueFrameworkOverrides extends VanillaFrameworkOverrides {
    constructor (parent) {
        super('vue');
        this.parent = parent;
    }
    // vue components are specified in the "components" part of the vue component - as such we need a way to
    // determine if a given component is within that context - this method provides this.
    // Note: This is only really used/necessary with cellRendererSelectors
    frameworkComponent (name, components) {
        let foundInstance = !!VueComponentFactory.searchForComponentInstance(this.parent, name, 10, true);
        let result = foundInstance ? name : null;
        if (!result && components && components[name]) {
            const indirectName = components[name];
            foundInstance = !!VueComponentFactory.searchForComponentInstance(this.parent, indirectName, 10, true);
            result = foundInstance ? indirectName : null;
        }
        return result;
    }
    isFrameworkComponent (comp) {
        return typeof comp === 'object';
    }
}

function getAgGridProperties () {
    const props = {
        gridOptions: {
            default () { return {}; },
        },
        rowDataModel: undefined,
    };
    const SHALLOW_CHECK_PROPERTIES = ['context', 'popupParent'];
    const DEEP_CHECK_PROPERTIES = ComponentUtil.ALL_PROPERTIES.filter((propertyName) => !SHALLOW_CHECK_PROPERTIES.includes(propertyName));

    const createPropsObject = (properties, component) => {
        const newProps = {};
        properties.forEach((propertyName) => {
            if (component[propertyName] === ComponentUtil.VUE_OMITTED_PROPERTY) return;
            newProps[propertyName] = component[propertyName];
        });
        return newProps;
    };

    const processPropsObject = (prev, current, component) => {
        if (!component.gridCreated || !component.api) return;
        const changes = {};
        Object.entries(current).forEach(([key, value]) => {
            if (prev[key] === value) return;
            changes[key] = value;
        });
        ComponentUtil.processOnChange(changes, component.api);
    };

    const computed = {
        props () { return createPropsObject(DEEP_CHECK_PROPERTIES, this); },
        shallowProps () { return createPropsObject(SHALLOW_CHECK_PROPERTIES, this); },
    };

    const watch = {
        rowDataModel (currentValue, previousValue) {
            if (!this.gridCreated || !this.api) return;
            if (currentValue === previousValue) return;
            if (currentValue && previousValue) {
                if (currentValue.length === previousValue.length) {
                    if (currentValue.every((item, index) => item === previousValue[index])) return;
                }
            }
            ComponentUtil.processOnChange({ rowData: currentValue }, this.api);
        },
        props: {
            handler (currentValue, previousValue) {
                processPropsObject(previousValue, currentValue, this);
            },
            deep: true,
        },
        // these props may be cyclic, so we don't deep check them.
        shallowProps: {
            handler (currentValue, previousValue) {
                processPropsObject(previousValue, currentValue, this);
            },
            deep: false,
        },
    };

    ComponentUtil.ALL_PROPERTIES.forEach((propertyName) => {
        props[propertyName] = { default: ComponentUtil.VUE_OMITTED_PROPERTY };
    });

    const model = {
        prop: 'rowDataModel',
        event: 'data-model-changed',
    };

    return [props, computed, watch, model];
}

const [props, computed, watch, model] = getAgGridProperties();

const AgGridVue = {
    name: 'ag-grid-vue',
    props,
    computed,
    watch,
    model,
    data () {
        return {
            gridCreated: false,
            isDestroyed: false,
            gridReadyFired: false,
            api: undefined,
            emitRowModel: null,
        };
    },
    render (h) {
        return h('div');
    },
    mounted () {
        this.emitRowModel = this.debounce(() => {
            this.$emit('data-model-changed', Object.freeze(this.getRowData()));
        }, 20);

        const frameworkComponentWrapper = new VueFrameworkComponentWrapper(this);
        const gridOptions = ComponentUtil.combineAttributesAndGridOptions(this.gridOptions, this);
        this.checkForBindingConflicts();
        const rowData = this.getRowDataBasedOnBindings();
        if (rowData !== ComponentUtil.VUE_OMITTED_PROPERTY) {
            gridOptions.rowData = rowData;
        }
        const gridParams = {
            globalEventListener: this.globalEventListenerFactory().bind(this),
            globalSyncEventListener: this.globalEventListenerFactory(true).bind(this),
            frameworkOverrides: new VueFrameworkOverrides(this),
            providedBeanInstances: { frameworkComponentWrapper },
            modules: this.modules,
        };
        this.api = createGrid(this.$el, gridOptions, gridParams);
        this.gridCreated = true;
    },
    destroyed () {
        if (this.gridCreated) {
            this.api?.destroy();
            this.isDestroyed = true;
        }
    },
    methods: {
        // It forces events defined in ALWAYS_SYNC_GLOBAL_EVENTS to be fired synchronously.
        // This is required for events such as GridPreDestroyed.
        // Other events are fired can be fired asynchronously or synchronously depending on config.
        globalEventListenerFactory (restrictToSyncOnly) {
            return (eventType, event) => {
                if (this.isDestroyed) return;
                if (eventType === 'gridReady') this.gridReadyFired = true;

                const alwaysSync = ALWAYS_SYNC_GLOBAL_EVENTS.has(eventType);
                if ((alwaysSync && !restrictToSyncOnly) || (!alwaysSync && restrictToSyncOnly)) return;

                this.updateModelIfUsed(eventType);
                const kebabName = kebabProperty(eventType);
                if (this.$listeners[kebabName]) {
                    this.$emit(kebabName, event);
                } else if (this.$listeners[eventType]) {
                    this.$emit(eventType, event);
                }
            };
        },
        checkForBindingConflicts () {
            if ((this.rowData || this.gridOptions.rowData) && this.rowDataModel) {
                console.warn('AG Grid: Using both rowData and rowDataModel. rowData will be ignored.');
            }
        },
        getRowData () {
            const rowData = [];
            this.api?.forEachNode((rowNode) => {
                rowData.push(rowNode.data);
            });
            return rowData;
        },
        updateModelIfUsed (eventType) {
            if (this.gridReadyFired && this.$listeners['data-model-changed'] && ROW_DATA_EVENTS.has(eventType)) {
                if (this.emitRowModel) this.emitRowModel();
            }
        },
        getRowDataBasedOnBindings () {
            return this.rowDataModel ? this.rowDataModel : (this.rowData ? this.rowData : this.gridOptions.rowData);
        },
        debounce (func, delay) {
            let timeout;
            return () => {
                window.clearTimeout(timeout);
                timeout = window.setTimeout(() => func(), delay);
            };
        },
    },
};

export { AgGridVue };
